import { useMemo, useState } from 'react'
import { ExternalUserServiceIntegrationConfig, ExternalUserServiceIntegrationUpdate } from '@zocks-communications/client-sdk-js'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useGetUserActiveSessionsQuery } from '../../../../../store/media/media'
import { IntegrationServiceId, PHONE_RECORDING_CAPABILITY } from '../../../../../common/integrations'
import { type ZoomSettingsFormValues } from './models'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { getEnforcedZoomMeetingCaptureType, isZoomStrictJoinEnforced, resolveZoomStrictJoinEnabled } from '../../../../../common/integrationRequirement'
import { ClickNavigationSection } from '../../../../../components/NavigationWithSideBar/ClickNavigation'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { ZoomMeeting } from './ZoomMeeting'
import { ZoomPhone } from './ZoomPhone'

export const THREE_HOUR_IN_SECONDS = 3 * 60 * 60

export const useZoomSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)!

  const { data: userData, isLoading: isUserLoading } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const { data: accountResponse, isLoading: isAccountLoading } = useGetAccountQuery({ accountId })
  const [startedAfter] = useState(() => Math.floor(Date.now() / 1000) - THREE_HOUR_IN_SECONDS)
  const { data: activeSessions, isLoading: activeSessionLoading } = useGetUserActiveSessionsQuery(
    {
      accountId: accountId,
      userId: authenticatedUser.id,
      kind: 'external',
      startedAfter: startedAfter,
      pageRequest: { page: 0, size: 10 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [updateUserIntegration, { isLoading: isUpdating }] = useUpdateUserIntegrationMutation()

  const meetingInProgress = useMemo(() => {
    if (!activeSessionLoading && activeSessions) {
      return activeSessions.data.length > 0
    }
    return false
  }, [activeSessionLoading, activeSessions])

  const zoomIntegration = useMemo(() => {
    return userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
  }, [userData])

  const isAutoJoinEnforced = useMemo(() => {
    if (zoomIntegration?.configuration?.type === 'bot') {
      return zoomIntegration.configuration.enforcedAutoJoin != null
    }
    return false
  }, [zoomIntegration])

  const isStrictJoinEnforced = useMemo(() => isZoomStrictJoinEnforced(accountResponse?.data), [accountResponse?.data])

  const enforcedMeetingCaptureType = useMemo(() => getEnforcedZoomMeetingCaptureType(accountResponse?.data), [accountResponse?.data])

  const resolvedZoomStrictJoinEnabled = useMemo(() => resolveZoomStrictJoinEnabled(userData, accountResponse?.data), [userData, accountResponse?.data])

  const save = async (data: ZoomSettingsFormValues, dirtyFields: Partial<Record<keyof ZoomSettingsFormValues, boolean>>) => {
    if (zoomIntegration) {
      const { removeSessionWhenNoRecording, autoJoin, strictJoinEnabled } = data
      const integrationType = enforcedMeetingCaptureType ?? data.integrationType
      // The BE replaces the whole configuration object, so an omitted field is stored as null.
      // Preserve the existing strict-join override verbatim (incl. null) when the user didn't touch the toggle.
      const existingStrictJoin = zoomIntegration.configuration.type === 'bot' ? zoomIntegration.configuration.strictJoinEnabled : null
      const payload = {
        configuration: {
          type: integrationType,
          ...(integrationType === 'bot' && !isAutoJoinEnforced && { autoJoin }),
          ...(integrationType === 'bot' &&
            !isStrictJoinEnforced && { strictJoinEnabled: dirtyFields.strictJoinEnabled ? strictJoinEnabled : existingStrictJoin }),
          ...(integrationType === 'recording' && { removeSessionWhenNoRecording }),
        } as ExternalUserServiceIntegrationConfig,
      } as ExternalUserServiceIntegrationUpdate

      await updateUserIntegration({
        accountId,
        integrationName: zoomIntegration.serviceId,
        integrationId: zoomIntegration.id,
        payload: payload,
      })
    }
  }

  return {
    zoomIntegration,
    isAutoJoinEnforced,
    isStrictJoinEnforced,
    enforcedMeetingCaptureType,
    resolvedZoomStrictJoinEnabled,
    meetingInProgress,
    isUserLoading,
    isUpdating,
    isFetching: isUserLoading || !userData || isAccountLoading || !accountResponse,
    save,
  }
}

export const useZoomPhoneSettings = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: userData, isLoading: isUserLoading } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateUserIntegration, { isLoading: isUpdating }] = useUpdateUserIntegrationMutation()

  const zoomIntegration = useMemo(() => userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM), [userData])

  const captureEnabled = zoomIntegration?.phoneConfiguration?.enabled ?? false
  const hasPhoneCapability = zoomIntegration?.capabilities?.includes(PHONE_RECORDING_CAPABILITY) ?? false

  const save = async (enabled: boolean) => {
    if (!zoomIntegration) {
      return
    }
    const payload = { phoneConfiguration: { enabled } } as ExternalUserServiceIntegrationUpdate
    await updateUserIntegration({
      accountId,
      integrationName: zoomIntegration.serviceId,
      integrationId: zoomIntegration.id,
      payload,
    })
  }

  return {
    captureEnabled,
    hasPhoneCapability,
    isFetching: isUserLoading || !userData,
    isUpdating,
    save,
  }
}

export const useZoomSettingsSections = (): ClickNavigationSection[] => {
  const { hasQuota } = useZocksQuota()
  const zoomPhoneEnabled = hasQuota('integration.zoomPhoneEnabled.value')

  return useMemo(() => {
    const sections: ClickNavigationSection[] = [{ id: 'settings', label: 'Zoom Meeting', content: () => <ZoomMeeting /> }]
    if (zoomPhoneEnabled) {
      sections.push({ id: 'phone', label: 'Zoom Phone', content: () => <ZoomPhone /> })
    }
    return sections
  }, [zoomPhoneEnabled])
}
